import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { OnBoardingRecord, Product, ServiceAndLocation } from '@voice/utils';
import { TableModule } from 'primeng/table';
import { WizardButtonsComponent } from './wizard-buttons.component';
import { CurrencyPipe, NgIf } from '@angular/common';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { MessageModule } from 'primeng/message';
import { BillingService } from '../../../services/billing.service';
import { HostedSummariesResponse } from '../../../models/billing.model';
import { mapNsToProductCode } from '../shared/utils/ns-mapping.utils';
import { ClientOnboardingService } from '../../../services/client-onboarding.service';
import { NotificationsService } from '../../../services/notifications.service';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { SendResultDialogComponent } from './send-result-dialog.component';
import {
  allLocationsAreValid,
  allServicesAreValid,
  allProductsAreValid,
} from './onboarding.utils';
import { firstValueFrom } from 'rxjs';
import { downloadCSV } from '../../../utils/file-manager';
import { MatchedProduct } from './interfaces/matched-product.interface';
import { parseEffectiveDate } from './utils/product-form.utils';
import { PermissionsService, PermissionCheck } from '../../../services/permissions.service';

@Component({
  selector: 'app-client-onboarding-summary',
  standalone: true,
  imports: [
    TableModule,
    WizardButtonsComponent,
    CurrencyPipe,
    NgIf,
    CardModule,
    ButtonModule,
    ConfirmDialogModule,
    ProgressSpinnerModule,
    TooltipModule,
    MessageModule,
  ],
  providers: [ConfirmationService, DialogService],
  templateUrl: './summary.component.html',
  styleUrls: ['./summary.component.scss'],
})
export class SummaryComponent implements OnInit, OnChanges {
  private readonly permissionsService = inject(PermissionsService);

  permissions: PermissionCheck = {
    canAdd: false,
    canEdit: false,
    canDelete: false,
    canView: false,
  };

  get canAdd(): boolean {
    return this.permissions.canAdd;
  }

  protected get sentLocationNames(): Set<string> {
    return new Set(
      (this.onBoardingRecord?.childCustomers || []).map((c) => c.locationName)
    );
  }

  protected get unsentLocations(): ServiceAndLocation[] {
    if (!this.onBoardingRecord?.parentCustomerId) return [];
    const sent = this.sentLocationNames;
    return (this.onBoardingRecord.locations || []).filter(
      (l) => !sent.has(l.companyName)
    );
  }

  protected get unsentLocationNames(): string {
    return this.unsentLocations.map((l) => l.companyName).join(', ');
  }

  protected get submissionPhase():
    | 'none'
    | 'has-missing'
    | 'customers-sent'
    | 'completed' {
    if (this.onBoardingRecord?.status === 'FINISHED') return 'completed';
    if (!this.onBoardingRecord?.parentCustomerId) return 'none';
    if (this.unsentLocations.length > 0) return 'has-missing';
    return 'customers-sent';
  }

  @Input()
  onBoardingRecord?: OnBoardingRecord;

  @Output()
  summaryReady: EventEmitter<string> = new EventEmitter<string>();
  @Output()
  processingStateChange = new EventEmitter<boolean>();

  hostedSummaries?: HostedSummariesResponse;
  products?: Product[];
  matchedProducts: MatchedProduct[] = [];
  loading = false;
  private onBoardingRecordId?: number;
  isFormValid = false;

  constructor(
    private readonly billingService: BillingService,
    private readonly clientOnboardingService: ClientOnboardingService,
    private readonly notificationService: NotificationsService,
    private readonly confirmationService: ConfirmationService,
    private readonly dialogService: DialogService
  ) {}

  async ngOnInit(): Promise<void> {
    await this.loadPermissions();
    if (
      this.onBoardingRecord?.id &&
      this.onBoardingRecord.id !== this.onBoardingRecordId
    ) {
      this.onBoardingRecordId = this.onBoardingRecord.id;
      this.loadRecordData();
    } else if (this.onBoardingRecord?.domain) {
      this.loadData();
    }
  }

  private async loadPermissions(): Promise<void> {
    this.permissions = await this.permissionsService.checkEntityPermissions('billing');
  }

  ngOnChanges(): void {
    if (
      this.onBoardingRecord?.id &&
      this.onBoardingRecord.id !== this.onBoardingRecordId
    ) {
      this.onBoardingRecordId = this.onBoardingRecord.id;
      this.loadRecordData();
    } else if (this.onBoardingRecord) {
      this.validateForm();
    }
  }

  private loadRecordData(): void {
    if (this.onBoardingRecord?.id) {
      this.clientOnboardingService.find(this.onBoardingRecord.id).subscribe({
        next: (record) => {
          this.onBoardingRecord = record;
          this.loadData();
          this.validateForm();
        },
        error: (err) => console.error('Error loading record:', err),
      });
    }
  }

  private loadData(): void {
    if (this.onBoardingRecord?.domain) {
      this.billingService.getProducts().subscribe({
        next: (products) => {
          this.products = products;
          this.matchProductsWithServices();
          this.validateForm();
        },
        error: (err) => console.error('Error loading products:', err),
      });
      if (
        !this.onBoardingRecord.products ||
        this.onBoardingRecord.products.length === 0
      ) {
        this.billingService
          .getHostedSummaries([this.onBoardingRecord.domain])
          .subscribe({
            next: (data) => {
              this.hostedSummaries = data;
              this.matchProductsWithServices();
              this.validateForm();
            },
            error: (err) =>
              console.error('Error loading hosted summaries:', err),
          });
      } else {
        this.matchProductsWithServices();
        this.validateForm();
      }
    }
  }

  private validateForm(): void {
    if (!this.onBoardingRecord) {
      this.isFormValid = false;
      return;
    }

    const locationsValid = allLocationsAreValid(
      this.onBoardingRecord.locations || []
    );
    const servicesValid = allServicesAreValid(
      this.onBoardingRecord.locations || []
    );
    const productsValid = allProductsAreValid(
      this.onBoardingRecord.products || []
    );

    this.isFormValid = locationsValid && servicesValid && productsValid;
  }

  private matchProductsWithServices(): void {
    this.matchedProducts = [];

    const products = this.onBoardingRecord?.products;
    if (products && products.length > 0) {
      this.matchProductsFromRecord();
    } else if (this.hostedSummaries?.rows && this.products) {
      this.matchProductsFromHostedSummaries();
    }
  }

  private matchProductsFromRecord(): void {
    if (!this.onBoardingRecord?.products) {
      return;
    }

    this.onBoardingRecord.products.forEach((locationProduct) => {
      const products = locationProduct.products;
      if (products && products.length > 0) {
        products.forEach((product) => {
          const effectiveDate = parseEffectiveDate(product.effectiveDate);
          this.matchedProducts.push({
            site: locationProduct.location || '',
            product: product.code || '',
            description: product.description || '-',
            rate: product.rate,
            quantity: product.quantity || 1,
            effectiveDate: effectiveDate
              ? effectiveDate.toISOString().split('T')[0]
              : undefined,
          });
        });
      }
    });
  }

  private matchProductsFromHostedSummaries(): void {
    if (!this.hostedSummaries?.rows || !this.products) {
      return;
    }

    this.hostedSummaries.rows.forEach((service) => {
      if (!service.SITE?.trim()) {
        return;
      }

      const productCode = mapNsToProductCode(
        service.SERVICE_TYPE,
        service.SCOPE
      );

      if (!productCode) {
        return;
      }

      const product = this.products?.find((p) => p.code === productCode);
      if (product?.code) {
        this.matchedProducts.push({
          site: service.SITE,
          product: product.code,
          description: product.description || '-',
          rate: product.rate,
          quantity: service.QUANTITY || 1,
        });
      }
    });
  }

  handleCommonButtonClicked(event: string) {
    this.summaryReady.emit(event);
  }

  protected handleSendCustomersAndSites(): void {
    if (!this.canAdd || !this.onBoardingRecord?.id) {
      if (!this.onBoardingRecord?.id) {
        this.notificationService.error('No onboarding record selected');
      }
      return;
    }

    const recordId = this.onBoardingRecord.id;

    this.confirmationService.confirm({
      header: 'Confirm: Send Customers & Sites',
      message:
        'This will create the parent customer and all sites in Rev.io. After this step, you will need to configure tax exemptions manually in Rev.io before sending the items.',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-primary',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => this.sendCustomersAndSites(recordId),
    });
  }

  private async sendCustomersAndSites(recordId: number): Promise<void> {
    this.loading = true;
    this.processingStateChange.emit(true);

    try {
      const result: any = await firstValueFrom(
        this.clientOnboardingService.sendCustomersAndSites(recordId)
      );

      if (result?.ok === false) {
        this.handleSendError(result);
      } else {
        this.loadRecordData();
      }
    } catch (error) {
      this.handleSendError(error);
    } finally {
      this.loading = false;
      this.processingStateChange.emit(false);
    }
  }

  protected handleSendMissingLocations(): void {
    if (!this.canAdd || !this.onBoardingRecord?.id) {
      if (!this.onBoardingRecord?.id) {
        this.notificationService.error('No onboarding record selected');
      }
      return;
    }

    const count = this.unsentLocations.length;
    const recordId = this.onBoardingRecord.id;

    this.confirmationService.confirm({
      header: 'Send Missing Locations',
      message: `${count} location(s) have not been sent to Rev.io yet. Send them now?`,
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-primary',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => this.sendMissingLocations(recordId),
    });
  }

  private async sendMissingLocations(recordId: number): Promise<void> {
    this.loading = true;
    this.processingStateChange.emit(true);

    try {
      const result: any = await firstValueFrom(
        this.clientOnboardingService.sendMissingLocations(recordId)
      );

      if (result?.ok === false) {
        this.handleSendError(result);
      } else {
        this.loadRecordData();
      }
    } catch (error) {
      this.handleSendError(error);
    } finally {
      this.loading = false;
      this.processingStateChange.emit(false);
    }
  }

  protected handleSendItems(): void {
    if (!this.canAdd || !this.onBoardingRecord?.id) {
      if (!this.onBoardingRecord?.id) {
        this.notificationService.error('No onboarding record selected');
      }
      return;
    }

    const recordId = this.onBoardingRecord.id;

    this.confirmationService.confirm({
      header: 'Confirm: Send Items to Rev.io',
      message:
        'Before proceeding, confirm that you have already configured tax exemptions in Rev.io for this customer.\n\nThis action will create all service products and cannot be easily undone. Have you completed the tax exemption configuration?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: "Yes, I've configured tax exemptions — Send Items",
      acceptButtonStyleClass: 'p-button-success',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => this.sendItems(recordId),
    });
  }

  private async sendItems(recordId: number): Promise<void> {
    this.loading = true;
    this.processingStateChange.emit(true);

    try {
      const result: any = await firstValueFrom(
        this.clientOnboardingService.sendItems(recordId)
      );

      if (result?.ok === false) {
        this.handleSendError(result);
      } else {
        this.showSuccessDialog(result);
      }
    } catch (error) {
      this.handleSendError(error);
    } finally {
      this.loading = false;
      this.processingStateChange.emit(false);
    }
  }

  async handleSendToRevio(): Promise<void> {
    if (!this.canAdd || !this.onBoardingRecord?.id) {
      if (!this.onBoardingRecord?.id) {
        this.notificationService.error('No onboarding record selected');
      }
      return;
    }

    try {
      const record = await this.loadOnboardingRecord(this.onBoardingRecord.id);
      const { locationsCount, productsCount } = this.calculateRecordCounts(record);

      if (!this.validateProductsExist(productsCount)) {
        return;
      }

      const confirmationMessage = this.buildConfirmationMessage(
        locationsCount,
        productsCount,
        record
      );

      await this.showConfirmationAndSend(confirmationMessage, this.onBoardingRecord.id);
    } catch (error) {
      this.handleLoadRecordError(error);
    }
  }

  /**
   * Loads the onboarding record from the service.
   */
  private async loadOnboardingRecord(recordId: number) {
    return await firstValueFrom(this.clientOnboardingService.find(recordId));
  }

  /**
   * Calculates counts of locations and products in the record.
   */
  private calculateRecordCounts(record: OnBoardingRecord): {
    locationsCount: number;
    productsCount: number;
  } {
    const locationsCount = record.locations?.length || 0;
    const productsCount =
      record.products?.reduce(
        (sum, lp) => sum + (lp.products?.length || 0),
        0
      ) || 0;

    return { locationsCount, productsCount };
  }

  /**
   * Validates that products exist in the record.
   */
  private validateProductsExist(productsCount: number): boolean {
    if (productsCount === 0) {
      this.notificationService.error(
        'No products found. Please add products in the Items step before sending.'
      );
      return false;
    }
    return true;
  }

  /**
   * Builds the confirmation dialog message.
   */
  private buildConfirmationMessage(
    locationsCount: number,
    productsCount: number,
    record: OnBoardingRecord
  ): string {
    const hasEffectiveDates = this.hasProductsWithEffectiveDates(record);
    const effectiveDateText = hasEffectiveDates
      ? '\n\nNote: Some products have custom effective dates set.'
      : '\n\nNote: Products will use today as the effective date (default).';

    return `Are you sure you want to send this client to Rev.io? This will create ${locationsCount} customer(s) and ${productsCount} product(s).${effectiveDateText}`;
  }

  /**
   * Checks if any products have effective dates set.
   */
  private hasProductsWithEffectiveDates(record: OnBoardingRecord): boolean {
    return (
      record.products?.some((lp) =>
        lp.products?.some((p) => p.effectiveDate)
      ) ?? false
    );
  }

  /**
   * Shows confirmation dialog and sends to Rev.io if confirmed.
   */
  private async showConfirmationAndSend(
    message: string,
    recordId: number
  ): Promise<void> {
    this.confirmationService.confirm({
      message,
      header: 'Confirm Send to Rev.io',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-success',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => this.sendToRevio(recordId),
    });
  }

  /**
   * Sends the onboarding record to Rev.io.
   */
  private async sendToRevio(recordId: number): Promise<void> {
    this.loading = true;
    this.processingStateChange.emit(true);

    try {
      const result: any = await firstValueFrom(
        this.clientOnboardingService.sendToRevio(recordId)
      );

      if (result.ok) {
        this.showSuccessDialog(result);
      } else {
        this.handleSendError(result);
      }
    } catch (error) {
      this.handleSendError(error);
    } finally {
      this.loading = false;
      this.processingStateChange.emit(false);
    }
  }

  /**
   * Handles errors when sending to Rev.io.
   */
  private handleSendError(resultOrError: any): void {
    const errorMessage =
      resultOrError instanceof Error
        ? resultOrError.message
        : resultOrError.errors?.map((e: any) => e.error).join(', ') ||
          resultOrError.error ||
          'Unknown error';

    this.notificationService.error(`Failed to send to Rev.io: ${errorMessage}`);
  }

  /**
   * Handles errors when loading the record.
   */
  private handleLoadRecordError(error: unknown): void {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error';
    this.notificationService.error(`Failed to load record: ${errorMessage}`);
  }

  private showSuccessDialog(result: any): void {
    const config: DynamicDialogConfig = {
      data: {
        parentCustomerId: result.parentCustomerId,
        childCustomers: result.childCustomers || [],
      },
      header: 'Success',
      modal: true,
      closable: true,
      width: '700px',
      contentStyle: {
        'overflow-y': 'auto',
      },
    };

    const dialogRef = this.dialogService.open(
      SendResultDialogComponent,
      config
    );

    dialogRef.onClose.subscribe({
      next: () => {
        this.summaryReady.emit('next');
      },
    });
  }

  exportSummary(): void {
    if (!this.onBoardingRecord) {
      this.notificationService.error('No data to export');
      return;
    }

    const exportData: any[] = [];

    // Export customer info
    if (this.onBoardingRecord.customer) {
      const customer = this.onBoardingRecord.customer as any;
      exportData.push({
        type: 'Customer',
        name: customer.name || '',
        email: customer.email || '',
        address: `${customer.addressLine1 || ''} ${
          customer.addressLine2 || ''
        }`.trim(),
        city: customer.city || '',
        state: customer.state || '',
        zipCode: customer.zipCode || '',
        customerClass: customer.customerClass || '',
        billingMethod: customer.billingMethod || '',
      });
    }

    // Export locations
    if (
      this.onBoardingRecord.locations &&
      this.onBoardingRecord.locations.length > 0
    ) {
      this.onBoardingRecord.locations.forEach((location) => {
        exportData.push({
          type: 'Location',
          companyName: location.companyName || '',
          firstName: location.firstName || '',
          lastName: location.lastName || '',
          address: `${location.addressLine1 || ''} ${
            location.addressLine2 || ''
          }`.trim(),
          city: location.city || '',
          state: location.state || '',
          zipCode: location.zipCode || '',
          provider: location.provider || '',
          serviceType: location.serviceType || '',
        });
      });
    }

    // Export products
    if (this.matchedProducts && this.matchedProducts.length > 0) {
      this.matchedProducts.forEach((product) => {
        exportData.push({
          type: 'Product',
          site: product.site || '',
          product: product.product || '',
          description: product.description || '',
          rate: product.rate !== undefined ? product.rate : '',
          quantity: product.quantity !== undefined ? product.quantity : '',
        });
      });
    }

    if (exportData.length === 0) {
      this.notificationService.error('No data to export');
      return;
    }

    const keys = [
      'type',
      'name',
      'email',
      'address',
      'city',
      'state',
      'zipCode',
      'customerClass',
      'billingMethod',
      'companyName',
      'firstName',
      'lastName',
      'provider',
      'serviceType',
      'site',
      'product',
      'description',
      'rate',
      'quantity',
    ];

    const fileName = `onboarding_summary_${
      this.onBoardingRecord.domain || 'export'
    }`;
    downloadCSV(fileName, keys, exportData);
  }
}
